import React, { useMemo, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import {
    FlexDiv,
    Heading2,
    Heading4,
    TextDefault,
    TextInput,
    ThemedButton,
    DropdownThemedButton,
} from '@awarego-ui/components';
import ThemedSuccessSVG from '../../themed/ThemedSuccessSVG';
import { AUTOMATION_SCHEDULE_TYPE, STATUS } from '../../../utils/constants';
import { formatDate } from '../../../utils/helpers';
import { Link } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/GetApp';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';

const LINK_VALIDITY_OPTIONS = [
    { key: '1h', value: '1h', name: '1 hour' },
    { key: '12h', value: '12h', name: '12 hours' },
    { key: '1d', value: '1d', name: '1 day' },
    { key: '1w', value: '1w', name: '1 week' },
    { key: '30d', value: '30d', name: '1 month' },
];

function StartScreen({
    createdTraining,
    training,
    signInLinksBaseUrl,
    defaultSigninLinkValidity = '1d',
    ongoing,
    isScorm = false,
    onDownloadPackage,
    isPackageDownloading,
}) {
    const [linkValidity, setLinkValidity] = useState(
        defaultSigninLinkValidity || '1d'
    );
    const handleDownloadPackage = useCallback(
        (type) => {
            if (!createdTraining) return;
            if (onDownloadPackage) {
                onDownloadPackage(
                    createdTraining.id,
                    createdTraining.uniqueId,
                    type
                );
            }
        },
        [createdTraining, onDownloadPackage]
    );

    const scormDownloadBtn = useMemo(() => {
        if (!createdTraining) return;
        return (
            <FlexDiv column gap={16} alignCenter>
                <DropdownThemedButton
                    text={
                        <FlexDiv gap={4} alignCenter>
                            {isPackageDownloading ? (
                                <CircularProgress
                                    size={20}
                                    thickness={4}
                                    color="inherit"
                                />
                            ) : (
                                <DownloadIcon fontSize="small" />
                            )}
                            Download training
                        </FlexDiv>
                    }
                    btnProps={{
                        mode: 'primary',
                        $center: true,
                        style: { display: 'inline-flex!important' },
                    }}
                    items={[
                        {
                            text: 'SCORM 1.2',
                            props: {
                                onClick: () => {
                                    handleDownloadPackage('scorm12');
                                },
                            },
                        },
                        {
                            text: 'SCORM 2004 3rd Edition',
                            props: {
                                onClick: () => {
                                    handleDownloadPackage('scorm2004');
                                },
                            },
                        },
                        {
                            text: 'cmi5',
                            props: {
                                onClick: () => {
                                    handleDownloadPackage('cmi5');
                                },
                            },
                        },
                    ]}
                />
            </FlexDiv>
        );
    }, [createdTraining, handleDownloadPackage, isPackageDownloading]);

    return (
        <FlexDiv alignCenter justifyCenter column fullHeight>
            <ThemedSuccessSVG />
            <Heading2 displayBlock my={16}>
                Success!
            </Heading2>
            <TextDefault lighter mb={16}>
                {createdTraining && createdTraining.name} training has{' '}
                {ongoing || training.status === STATUS.SCHEDULED
                    ? 'been successfully updated!'
                    : 'successfully started!'}
                {!training.isScorm &&
                    training.scheduleType === AUTOMATION_SCHEDULE_TYPE.FUTURE &&
                    !ongoing &&
                    ` Training invitation will be sent on ${
                        training.deliverySchedule &&
                        training.deliverySchedule.length > 0 &&
                        formatDate(training.deliverySchedule[0].date)
                    }`}
            </TextDefault>
            {training.scheduleType ===
                AUTOMATION_SCHEDULE_TYPE.DOWNLOAD_LINKS && (
                <FlexDiv column mb={16} gap={16} alignCenter>
                    <FlexDiv column maxWidth={264} alignSelf="center">
                        <Heading4 lighter mb={8}>
                            Link validity
                        </Heading4>
                        <TextInput
                            select
                            value={linkValidity}
                            onChange={(e) => setLinkValidity(e.target.value)}
                            fullWidth
                        >
                            {LINK_VALIDITY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.value}>
                                    {opt.name}
                                </MenuItem>
                            ))}
                        </TextInput>
                    </FlexDiv>
                    <ThemedButton
                        mode="primary"
                        as="a"
                        href={`${signInLinksBaseUrl}&linkValidity=${linkValidity}`}
                        rel="noopener noreferrer"
                        target="_blank"
                        text={
                            <>
                                Download sign in links{' '}
                                <DownloadIcon fontSize="small" />
                            </>
                        }
                    />
                </FlexDiv>
            )}
            {!isScorm ? (
                <Link
                    to={`/trainings/${isScorm ? 'p' : ''}${
                        createdTraining.id
                    }/view`}
                >
                    <ThemedButton
                        mode="primary"
                        text="Go to training overview"
                    />
                </Link>
            ) : (
                scormDownloadBtn
            )}
        </FlexDiv>
    );
}

export default observer(StartScreen);
